/**
 * IMPORTS
 */
import accountsSelectors from 'src/aggregates/accounts/selectors';
import {ApplicationError} from 'src/aggregates/err/applicationerror';
import * as conversationsAPI from 'src/aggregates/conversations/api';
import {resetError} from 'src/aggregates/conversations/api';
import {stopForward} from 'src/aggregates/conversations/api';
import conversations from  'src/aggregates/conversations/selectors';
import {createNotificationBody} from 'src/aggregates/conversations/utils';
import {messageTypesIdToName} from 'src/aggregates/conversations/utils';
import {tryToFree} from 'src/aggregates/conversations/utils';
import customers from 'src/aggregates/customers/selectors';
import {contactTypesChannelToId} from 'src/aggregates/customers/utils';
import {ensureCustomer} from 'src/aggregates/customers/utils';
import {loadByContact} from 'src/aggregates/timelines/api';
import {onMessageAck as timelineOnMessageAck}
    from 'src/aggregates/timelines/api';
import {getConversation as getTimeline}
    from 'src/aggregates/timelines/selectors';
import * as userAPI from 'src/aggregates/user/api';
import {fetch as fetchUser} from 'src/aggregates/user/selectors';
import {responder as router} from 'src/aggregates/user/xmpp/main';
import history from 'src/history';
import {logger} from 'src/logger';
import State from 'src/store';
import {focus} from 'src/utils/focus';
import {handleNumberMismatch} from 'src/utils/handleNumberMismatch';
import {showMessageFailurePopup} from 'src/utils/popup';
import {showNumberMismatchPopup} from 'src/utils/popup';


/**
 * TYPES
 */
import {Request} from 'requestis';
import {IPostConversationsIdMessagesId}
    from 'src/aggregates/conversations/responders.d';
import {IPutConversationsIdMessagesArgs}
    from 'src/aggregates/conversations/responders.d';
import {getTime} from 'date-fns';


/**
 * CONSTANTS AND DEFINITIONS
 */
const DELETE_CHATS_ID = 'chats/:id';
const CONVERSATION_ID_MESSAGES = 'conversations/:id/messages';
const CONVERSATION_ID_MESSAGES_ECHO = 'conversations/:id/messages/echo';
const CONVERSATION_ID_MESSAGES_ID = 'conversations/:id/messages/:messageId';
const CONVERSATION_ID_MESSAGES_ID_MISMATCH
    = CONVERSATION_ID_MESSAGES_ID + '/mismatch';
const POST_CONVERSATION_ECHO = 'conversations/echo';
const POST_CONVERSATION_OUTGOING = 'conversations/outgoing';
const PUT_CHATS_ID = 'chats/:id';
const PUT_MESSAGES_FAIL = '/conversations/:id/messages/:messageId/fail';

// failures which lead to a popup message
const POPUP_FAILURES = [
    'ContactDoesNotExist',
    'MessageRejected',
    'PaymentRelatedFailure',
    'TemplateRequired',
];


/**
 * CODE
 */

/**
 * I finish a conversation.
 *
 * :param request: request to be responded
 * :param args: request parameters
 *
 * :returns: nothing
 */
async function _RH_DeleteChatsId (
    request: any,
    args: any,
): Promise<void>
{
    // get id
    const id = Number(args.id);

    // get exit status
    const status = Number(request.params.status);

    // get conversation end
    const end = request.params.end === undefined ?
        undefined : request.params.end * 1000 || null;

    // get conversation last interaction timestamp
    const lastInteraction = request.params.lastInteraction;

    // get conversation current application uid
    const uid = request.params.application ?
        request.params.application.uid :
        null;

    // request is not proper: fail
    if (isNaN(id) ||
        isNaN(status) ||
        end === undefined ||
        lastInteraction === undefined ||
        uid === undefined)
    {
        request.fail(400, {});
        return;
    }

    // request has proper parameters: exit conversation
    await conversationsAPI.onExit(id, status, end, lastInteraction, uid);

    // respond with success
    request.respond(200, {});
}


/* eslint-disable complexity */
/**
 * I handle a received message by sending it to target conversation.
 *
 * :param request: request to be responded
 * :param args: custom message args
 *
 * :returns: promise with nothing
 */
async function _RH_PostConversationsIdMessages (
    request: Request,
    args: IPutConversationsIdMessagesArgs,
): Promise<void> // eslint-disable no-complexity
{
    // get conversation id
    const conversation = Number(args.id);

    // request is not proper: fail
    if (request.params.ack === undefined ||
        request.params.body === undefined ||
        request.params.id === undefined ||
        request.params.class === undefined ||
        request.params.type === undefined ||
        request.params.name === undefined ||
        request.params.originalId === undefined ||
        request.params.timestamp === undefined ||
        request.params.uid === undefined ||
        request.params.unansweredMessages === undefined ||
        isNaN(conversation))
    {
        request.fail(400, {});
        return;
    }

    // get body
    const {body} = request.params;

    // send message to conversation
    await conversationsAPI.onMessage(request.params.ack,
                                     body,
                                     request.params.caption,
                                     conversation,
                                     request.params.id,
                                     request.params.class,
                                     request.params.type,
                                     request.params.name,
                                     request.params.originalId,
                                     request.params.referral,
                                     request.params.reply,
                                     request.params.timestamp,
                                     request.params.uid,
                                     request.params.unansweredMessages,
                                     request.params.mode);

    // get state
    const state = State.getState();

    // get conversation channel id
    const channel = contactTypesChannelToId.get(
        state.conversation.byId[request.params.conversation].channel,
    );

    // get conversation customer
    const customer = customers.getCustomerByContact(channel,
                                                    request.params.uid);

    // get message formatting
    const formatting = messageTypesIdToName.get(request.params.type);

    // notify user that a new message has arrived
    await userAPI.notify(createNotificationBody(body, formatting),
                         conversation,
                         true,
                         customer.name);

    // respond with success
    request.respond(200, {});
}
/* eslint-enable complexity */


/**
 * I handle a received echo message.
 *
 * :param request: request to be responded
 * :param args: custom message args
 *
 * :returns: promise with nothing
 */
async function _RH_PostConversationsIdMessagesEcho (
    request: Request,
    args: IPutConversationsIdMessagesArgs,
): Promise<void>
{
    // get conversation id
    const conversation = Number(args.id);

    // request is not proper: fail
    if (request.params?.ack === undefined ||
        request.params?.body === undefined ||
        request.params?.id === undefined ||
        request.params?.class === undefined ||
        request.params?.type === undefined ||
        request.params?.name === undefined ||
        request.params?.originalId === undefined ||
        request.params?.timestamp === undefined ||
        request.params?.uid === undefined ||
        request.params?.unansweredMessages === undefined ||
        request.params?.outgoing === undefined ||
        isNaN(conversation))
    {
        request.fail(400, {});
        return;
    }

    // send message to conversation
    await conversationsAPI.onEchoMessage(
        request.params.ack,
        request.params.body,
        request.params.caption,
        conversation,
        request.params.id,
        request.params.class,
        request.params.type,
        request.params.name,
        request.params.originalId,
        request.params.referral,
        request.params.reply,
        request.params.timestamp,
        request.params.uid,
        request.params.unansweredMessages,
        request.params.mode,
        request.params.outgoing,
    );

    // respond with success
    request.respond(200, {});
}


/**
 * I handle an ack for a message.
 *
 * :param request: request to be responded
 * :param args: custom message args
 *
 * :returns: promise with nothing
 */
async function _RH_PutConversationsIdMessagesId (
    request: Request,
    args: IPostConversationsIdMessagesId,
): Promise<void>
{
    // set conversation and message id
    let conversationId: number;
    let messageId: number;

    // convert conversation id and message id to number
    try
    {
        conversationId = Number(args.id);
        messageId = Number(args.messageId);
    }

    // conversion failed: return 400 status fail and log
    catch
    {
        request.fail(400, {});
        logger.error('CVJPZ0022E', {});
        return;
    }


    // request is not proper: fail
    if (request.params.ack === undefined ||
        request.params.originalId === undefined)
    {
        request.fail(400, {});
        logger.error('CVJPZ0023E', {});
        return;
    }

    // search for conversation on conversation aggregate
    const conversation = conversations.get(conversationId);

    // conversation found: call conversations api
    if (conversation !== null)
    {
        // receive message ack
        await conversationsAPI.onMessageAck(conversationId,
                                            messageId,
                                            request.params.ack,
                                            request.params.originalId);
    }

    // conversation not found: search in timeline aggregate
    else
    {
        // search for conversation on timeline aggregate
        const timelineConversation = getTimeline(conversationId);

        // conversation found in timeline: call timeline api
        if (timelineConversation !== null)
        {
            // receive message ack
            await timelineOnMessageAck(timelineConversation.id,
                                       messageId,
                                       request.params.ack,
                                       request.params.originalId);
        }
    }

    // respond with success
    request.respond(200, {});
}


/**
 * I handle a message sent to a mismatch number.
 *
 * :param request: request to be responded
 * :param args: custom message args
 *
 * :returns: promise with nothing
 */
async function _RH_PutConversationsIdMessagesIdMismatch (
    request: Request,
    args: IPostConversationsIdMessagesId,
): Promise<void>
{
    // get user id
    const user = fetchUser().id;

    // set conversation and message id
    let conversationId: number;

    // uid to be received from request path
    let uid: string;

    // attempt to read conversation id and uid
    try
    {
        conversationId = Number(args.id);
        uid = request.params.uid;

        // request is not proper: fail
        if (uid === undefined)
        {
            throw new ApplicationError('Uid is not defined');
        }
    }

    // conversion failed: return 400 status fail and log
    catch
    {
        request.fail(400, {});
        logger.error('CVJPZ0022E', {
            reason: 'Bad request',
            user,
        });
        return;
    }

    // search for conversation on conversations or timelines
    const conversation =
        conversations.get(conversationId) ??
        getTimeline(conversationId);

    // conversation not found: fail
    if (conversation === null)
    {
        request.fail(404, {});
        logger.error('CVJPZ0022E', {
            reason: 'Conversation not found',
            user,
        });
        return;
    }

    // not a mismatch: respond successfully
    if (conversation.remote === uid)
    {
        request.respond(204, {});
        return;
    }

    // respond with success
    request.respond(202, {});

    // call utility function to handle mismatch
    await handleNumberMismatch(
        conversation.account,
        conversation.id,
        uid,
        conversation.remote,
    );

    // show popup to inform user
    showNumberMismatchPopup(uid);
}


/**
 * I handle the add of a conversation.
 *
 * :param request: request to be responded
 * :param args: request parameters
 *
 * :returns: nothing
 */
async function _RH_PutChatsId (
    request: any,
    args: any,
): Promise<void>
{
    // convert conversation id to number
    const conversation = Number(args.id);

    // request is not proper: fail
    if (request.params.account === undefined ||
        request.params.channel === undefined ||
        request.params.contactId === undefined ||
        request.params.remote === undefined ||
        request.params.name === undefined ||
        request.params.lastInteraction === undefined ||
        request.params.start === undefined ||
        request.params.subject === undefined ||
        request.params.unansweredMessages === undefined ||
        isNaN(conversation))
    {
        request.fail(400, {});
        logger.error('CVJPZ0084E', {
            conversation,
            reason: 'Bad request',
            user: fetchUser().id,
        });
        return;
    }

    // get account data
    const account = accountsSelectors.get(request.params.account);

    // account not found: fail
    if (account === null)
    {
        const reason = `Account "${request.params.account}" not visible`;
        request.fail(403, {reason});
        logger.error('CVJPZ0084E', {
            conversation,
            reason,
            user: fetchUser().id,
        });
        return;
    }

    // proper request: create conversation on store
    await conversationsAPI.onCreate(request.params.account,
                                    request.params.channel,
                                    request.params.contactId,
                                    request.params.remote,
                                    request.params.hasAgentAnswer,
                                    conversation,
                                    request.params.name,
                                    request.params.lastInteraction,
                                    request.params.start,
                                    request.params.subject,
                                    false,
                                    null,
                                    request.params.unansweredMessages);

    // respond with successfully created
    request.respond(201, {});

    // get conversation channel id
    const channel = contactTypesChannelToId.get(request.params.channel);

    // get current customer
    const customer = customers.getCustomerByContact(channel,
                                                    request.params.remote);

    // notify user of incoming conversation
    userAPI.notify(createNotificationBody('', 'conversation'),
                   conversation,
                   true,
                   customer?.name ?? request.params.remote);
}


/**
 * I handle an error sending the message.
 *
 * :param request: request to be responded
 * :param args: custom message args

 * :returns: nothing
 */
async function _RH_PutConversationsIdMessagesIdFail (
    request: Request,
    args: IPostConversationsIdMessagesId,
): Promise<void>
{
    // get user id and company
    const {id, company} = fetchUser();

    // set conversation id
    let conversationId: number;

    // get messageId
    let messageId: number;

    // attempt to read conversation id
    try
    {
        conversationId = parseInt(args.id);
        messageId = parseInt(args.messageId);
    }

    // bad conversation or message id: log, fail and abort
    catch
    {
        request.fail(400, {});
        logger.error('CVJPZ0053E', {company, id, reason: 'Bad request'});
        return;
    }

    // get error request
    const error = request.params.error;

    // get data request
    const data = request.params.data;

    // respond success
    request.respond(202, {});

    // a popup message is needed: show it
    if (POPUP_FAILURES.includes(error) === true)
    {
        showMessageFailurePopup(error, data, conversationId, messageId);
    }
}


/**
 * I handle the creation of an echo conversation.
 *
 * :param request: request to be responded
 *
 * :returns: nothing
 */
async function _RH_PostConversationsEcho (request: Request): Promise<void>
{

    // get user id
    const user = fetchUser().id;

    // request is not proper: fail as bad request and return
    if (request.params?.account === undefined ||
        request.params?.channel === undefined ||
        request.params?.contact === undefined ||
        request.params?.contactName === undefined ||
        request.params?.conversation === undefined ||
        request.params?.lastInteraction === undefined ||
        request.params?.remote === undefined ||
        request.params?.start === undefined ||
        request.params?.subject === undefined ||
        request.params?.templateRequired === undefined ||
        request.params?.templateSent === undefined)
    {
        request.fail(400, {});
        logger.error('CVJPZ0073E', {
            reason: `Bad request: ${request.params}`,
            user,
        });
        return;
    }

    // get request params
    const {
        account: accountId,
        channel,
        contact,
        contactName,
        conversation,
        lastInteraction,
        remote: contactUid,
        start,
        templateRequired,
        templateSent,
        subject,
    } = request.params;

    // get account
    const account = accountsSelectors.get(accountId);


    // account doesn't exist: fail as not found and return
    if (account === null)
    {
        request.fail(404, {});
        logger.error('CVJPZ0073E', {
            reason: `Account ${accountId} not found`,
            user,
        });
        return;
    }

    // ensure customer is in the store
    await ensureCustomer(
        channel,
        contactName,
        contactUid,
        true,
    );

    // create echo conversation on store without changing agent focus
    await conversationsAPI.onEchoCreate(
        accountId,
        channel,
        contact,
        contactUid,
        true,
        conversation,
        contactName,
        lastInteraction,
        start,
        subject,
        templateRequired,
        templateSent,
        0,
    );

    // respond with successfully created
    request.respond(201, {});
}


/**
 * I handle the creation of an outgoing conversation.
 *
 * :param request: request to be responded
 *
 * :returns: nothing
 */
async function _RH_PostConversationsOutgoing (request: Request): Promise<void>
{
    // get user id
    const user = fetchUser().id;

    // request is not proper: fail as bad request and return
    if (request.params?.account === undefined ||
        request.params?.channel === undefined ||
        request.params?.contact === undefined ||
        request.params?.contactName === undefined ||
        request.params?.conversation === undefined ||
        request.params?.lastInteraction === undefined ||
        request.params?.remote === undefined ||
        request.params?.start === undefined ||
        request.params?.subject === undefined ||
        request.params?.templateRequired === undefined ||
        request.params?.templateSent === undefined)
    {
        request.fail(400, {});
        logger.error('CVJPZ0048E', {
            reason: `Bad request: ${request.params}`,
            user,
        });
        return;
    }

    // get request params
    const {
        account: accountId,
        channel,
        contact,
        contactName,
        conversation,
        lastInteraction,
        remote: contactUid,
        start,
        templateRequired,
        templateSent,
        subject,
    } = request.params;

    // get account
    const account = accountsSelectors.get(accountId);

    // account doesn't exist: fail as not found and return
    if (account === null)
    {
        request.fail(404, {});
        logger.error('CVJPZ0048E', {
            reason: `Account ${accountId} not found`,
            user,
        });
        return;
    }

    // stop forward messages
    await stopForward();

    // ensure customer is in the store and get its id
    const customer = await ensureCustomer(
        channel,
        contactName,
        contactUid,
        true,
    );

    // get contact type
    const contactType = contactTypesChannelToId.get(channel?.toLowerCase());

    // conversation already exists: create it on store
    if (conversation !== null)
    {
        // create conversation
        await conversationsAPI.onCreate(
            accountId,
            channel,
            contact,
            contactUid,
            true,
            conversation,
            contactName,
            lastInteraction,
            start,
            null,
            templateRequired,
            templateSent,
            0,
        );
    }

    // conversation doesn't exist: create it from scratch
    else
    {
        await conversationsAPI.create(accountId,
                                      contactUid,
                                      subject,
                                      contactType);
    }

    // get contact id
    const contactId = customers.getContactByChannelAndUid(
        contactType,
        contactUid,
    )?.id ?? null;

    // focus screen
    await focus(accountId, contactId, conversation, customer);

    // reset conversation errors
    await resetError();

    // push url to customer conversation's channel
    history.push(`/dashboard/${customer}/${channel}/${contactType}`);

    // get notification's body
    const notification = createNotificationBody('', 'conversation');

    // get customer's name
    const {name} = customers.fetch(customer);

    // notify user of created conversation
    await userAPI.notify(notification, 0, true, name);

    // respond successfully
    request.respond(200, {});

    // try to free conversation
    await tryToFree(accountId, contactUid);

    // load contact timeline
    await loadByContact(contactId);
}


/**
 * I register endpoints.
 *
 * :returns: nothing
 */
function start (): void
{
    // register endpoints
    router.register('DELETE',
                    DELETE_CHATS_ID,
                    _RH_DeleteChatsId);
    router.register('POST',
                    CONVERSATION_ID_MESSAGES,
                    _RH_PostConversationsIdMessages);
    router.register('POST',
                    CONVERSATION_ID_MESSAGES_ECHO,
                    _RH_PostConversationsIdMessagesEcho);
    router.register('POST',
                    POST_CONVERSATION_ECHO,
                    _RH_PostConversationsEcho);
    router.register('POST',
                    POST_CONVERSATION_OUTGOING,
                    _RH_PostConversationsOutgoing);
    router.register('PUT',
                    CONVERSATION_ID_MESSAGES_ID,
                    _RH_PutConversationsIdMessagesId);
    router.register('PUT',
                    CONVERSATION_ID_MESSAGES_ID_MISMATCH,
                    _RH_PutConversationsIdMessagesIdMismatch);
    router.register('PUT',
                    PUT_CHATS_ID,
                    _RH_PutChatsId);
    router.register('PUT',
                    PUT_MESSAGES_FAIL,
                    _RH_PutConversationsIdMessagesIdFail);
}


/**
 * I unregister endpoints.
 *
 * :returns: nothing
 */
function stop (): void
{
    // unregister endpoints
    router.unregister('DELETE', DELETE_CHATS_ID);
    router.unregister('POST', CONVERSATION_ID_MESSAGES);
    router.unregister('POST', CONVERSATION_ID_MESSAGES_ECHO);
    router.unregister('POST', POST_CONVERSATION_ECHO);
    router.unregister('POST', POST_CONVERSATION_OUTGOING);
    router.unregister('PUT', CONVERSATION_ID_MESSAGES_ID);
    router.unregister('PUT', CONVERSATION_ID_MESSAGES_ID_MISMATCH);
    router.unregister('PUT', PUT_CHATS_ID);
    router.unregister('PUT', PUT_MESSAGES_FAIL);
}


/**
 * EXPORTS
 */
export {
    start,
    stop,
};
